import { Button, TextButton } from '@creditornot/cb-button';
import { CopyPaste } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { COOKIE, EVENT, QUERY_PARAM } from './constants';
import * as cookie from './cookie';
import dispatchActivePreviewChangeEvent from './dispatchActivePreviewChangeEvent';
import { ActivePreviewChangeEvent } from './types';

type ActivePreviewChangeEventListener = (event: ActivePreviewChangeEvent) => void;

const subscribe = (listener: ActivePreviewChangeEventListener): (() => void) => {
  window.document.addEventListener(EVENT, listener);

  return () => {
    window.document.removeEventListener(EVENT, listener);
  };
};

const getShareUrl = (previewNumber: number) => {
  const params = new URLSearchParams(window.location.search);
  params.set(QUERY_PARAM, previewNumber.toString());
  const query = `?${params.toString()}`;
  return `${window.location.origin}${window.location.pathname}${query}${window.location.hash}`;
};

const getPreviewNumberFromQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const previewNumber = params.get(QUERY_PARAM);
  return previewNumber ? parseInt(previewNumber, 10) : null;
};

const getQueryParamsWithoutPreviewNumber = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete(QUERY_PARAM);
  return params;
};

const Root = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 32px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const CopyButton = styled(TextButton).attrs({
  icon: <CopyPaste />,
  size: 'small',
})``;

const ReloadButton = styled(Button).attrs({
  size: 'small',
  variant: 'blue',
})``;

const BackButton = styled(Button).attrs({
  size: 'small',
  variant: 'white',
})``;

const Buttons = styled.div`
  ${ReloadButton}, ${BackButton} {
    margin-inline-start: 16px;
  }
`;

const DeveloperPreviewIndicator = () => {
  const history = useHistory();

  const [initialPreviewNumber] = useState(() => {
    const string = cookie.get(COOKIE);
    return string ? parseInt(string, 10) : undefined;
  });
  const [previewNumber, setPreviewNumber] = useState<number | undefined>(initialPreviewNumber);

  useEffect(() => {
    return subscribe(event => {
      setPreviewNumber(event.detail.number);
    });
  }, []);

  useEffect(() => {
    const previewNumber = getPreviewNumberFromQueryParams();
    if (previewNumber) {
      dispatchActivePreviewChangeEvent(previewNumber);
      const params = getQueryParamsWithoutPreviewNumber();
      history.replace({
        pathname: window.location.pathname,
        search: params.toString(),
        hash: window.location.hash,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (previewNumber) {
    const isPending = initialPreviewNumber !== previewNumber;

    return (
      <Root>
        <T.Body3>{initialPreviewNumber ? `Previewing #${initialPreviewNumber}` : null}</T.Body3>

        <Buttons>
          <CopyButton
            onClick={() => {
              const shareUrl = getShareUrl(previewNumber);
              window.navigator.clipboard.writeText(shareUrl);
            }}
          >
            Copy preview link
          </CopyButton>

          {isPending && (
            <ReloadButton
              onClick={() => {
                cookie.set(COOKIE, `${previewNumber}`);
                window.location.reload();
              }}
            >
              Reload as #{previewNumber}
            </ReloadButton>
          )}

          {initialPreviewNumber && (
            <BackButton
              onClick={() => {
                cookie.remove(COOKIE);
                window.location.reload();
              }}
            >
              Back to {process.env.APP_ENV}
            </BackButton>
          )}
        </Buttons>
      </Root>
    );
  }

  return null;
};

export default DeveloperPreviewIndicator;
